import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { BlogOverviewSidebar } from '@/components/blog/BlogOverviewSidebar';
import { BlogCard } from '@/components/blog/BlogCard';
import PageBreadcrumbs from '@/components/PageBreadcrumbs';
import StructuredData from '@/components/StructuredData';
import { getBreadcrumbSchema, getBlogSchema } from '@/utils/seoSchemas';
export default function Blog() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const categoryFilter = searchParams.get('category');
  const tagFilter = searchParams.get('tag');
  const {
    data: posts,
    isLoading,
    error
  } = useQuery({
    queryKey: ['public-blog-posts', categoryFilter, tagFilter],
    queryFn: async () => {
      let query = supabase.from('blog_posts').select('id, title, slug, excerpt, created_at, meta_description, category, tags').eq('published', true).order('created_at', {
        ascending: false
      });

      // Apply category filter
      if (categoryFilter) {
        // Normalisiere den Filter (z.B. "cyberversicherung" → "Cyberversicherung")
        const categoryName = categoryFilter.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
        query = query.ilike('category', categoryName);
      }

      // Apply tag filter
      if (tagFilter) {
        // Kapitalisiere den Tag-Namen (z.B. "versicherung" → "Versicherung")
        const tagName = tagFilter.replace(/-/g, ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
        query = query.contains('tags', [tagName]);
      }
      const {
        data,
        error
      } = await query;
      if (error) throw error;
      return data;
    }
  });

  // Excerpt generieren falls nicht vorhanden
  const getExcerpt = (post: any) => {
    // Priorisierung: excerpt > meta_description > Standardtext
    if (post.excerpt && post.excerpt.trim() !== '') return post.excerpt;
    if (post.meta_description && post.meta_description.trim() !== '') return post.meta_description;
    return 'Lesen Sie mehr in diesem interessanten Artikel über Cyberversicherungen für Arztpraxen.';
  };
  if (isLoading) {
    return <>
        <Navigation />
        <div className="container mx-auto px-4 py-16">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">Blog</h1>
            <p className="text-muted-foreground">Lädt Artikel...</p>
          </div>
        </div>
        <Footer />
      </>;
  }
  if (error) {
    return <>
        <Navigation />
        <div className="container mx-auto px-4 py-16">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">Blog</h1>
            <p className="text-destructive">
              Fehler beim Laden der Artikel. Bitte versuchen Sie es später erneut.
            </p>
          </div>
        </div>
        <Footer />
      </>;
  }
  const breadcrumbSchema = getBreadcrumbSchema([{
    name: "Home",
    url: "https://praxis-versichert.de"
  }, {
    name: "Blog"
  }]);
  const blogSchema = getBlogSchema(posts?.map(p => ({
    title: p.title,
    slug: p.slug,
    created_at: p.created_at
  })) || []);
  return <>
      <StructuredData data={[breadcrumbSchema, blogSchema]} />
      <Navigation />
      <PageBreadcrumbs items={[{
      label: "Home",
      path: "/"
    }, {
      label: "Blog"
    }]} />
      
      {/* Wellenförmiger Hintergrund */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_120%_100%_at_50%_20%,hsl(var(--accent)/0.08)_0%,hsl(var(--accent)/0.04)_30%,transparent_60%)] opacity-60"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_120%_100%_at_50%_60%,hsl(var(--accent)/0.10)_0%,hsl(var(--accent)/0.05)_35%,transparent_65%)] opacity-70"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_100%_80%_at_50%_90%,hsl(var(--accent)/0.06)_0%,hsl(var(--accent)/0.02)_40%,hsl(var(--background))_75%)]"></div>
      </div>

      <div className="min-h-screen relative z-10">
        {/* Header */}
        <div className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 border-b border-white/5">
          <div className="container mx-auto text-center max-w-4xl">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 text-foreground">
              Blog
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-muted-foreground/90 leading-relaxed px-4 sm:px-0">
              Aktuelle Insights, Tipps und News rund um Cyberversicherungen für Arztpraxen
            </p>
          </div>
        </div>

        {/* Cards Grid with Sidebar */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 sm:gap-8 max-w-7xl mx-auto">
            {/* Main Content - Blog Posts */}
            <div className="space-y-6 sm:space-y-8">
              {posts && posts.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                  {posts.map((post, index) => <BlogCard key={post.id} post={post} index={index} />)}
                </div> : <div className="text-center py-20">
                  <h2 className="text-3xl font-bold mb-4">
                    {categoryFilter || tagFilter ? 'Keine Artikel gefunden' : 'Noch keine Artikel'}
                  </h2>
                  <p className="text-muted-foreground text-lg">
                    {categoryFilter || tagFilter ? 'Für diesen Filter sind keine Artikel verfügbar.' : 'Die ersten Blog-Artikel sind in Vorbereitung.'}
                  </p>
                </div>}
            </div>
            
            {/* Sidebar */}
            <aside className="lg:sticky lg:top-24 h-fit order-first lg:order-last">
              <BlogOverviewSidebar />
            </aside>
          </div>
        </div>
      </div>
      <Footer />
    </>;
}